import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { FlagDefinition } from '@zag/feature-flags/common';
import { Avatar, Card, TranslatedPipe } from '@zag/ui';

const MAX_VISIBLE_FEATURES = 6;

/**
 * Card-mode presentation of a feature-flag definition — mirrors
 * LicenceDefinitionCardComponent from `@zag/licensing/ui`. The whole
 * card is clickable (routes to the editor). Flags have no `active`
 * field, so unlike the licensing card there is no header switch —
 * flag rollout is controlled per-tenant via TenantActiveFlags.
 */
@Component({
  selector: 'zag-flag-definition-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Avatar, Card, TranslatedPipe, TranslatePipe],
  template: `
    <zag-card variant="interactive" (cardClick)="edit.emit(definition())">
      <div card-header class="header">
        <zag-avatar class="header__avatar" [name]="definition().name" size="md" variant="warning" />
        <div class="identity">
          <span class="identity__key">{{ definition().key }}</span>
          <h3 class="identity__name">{{ definition().translations | zagTranslated: 'name' : definition().name }}</h3>
        </div>
      </div>

      @if (definition().translations | zagTranslated: 'description' : definition().description; as description) {
        <p class="description">{{ description }}</p>
      }

      <div class="features">
        @if (definition().features.length === 0) {
          <span class="feature-tag feature-tag--empty">
            {{ 'control.featureFlags.definitions.card.noFeatures' | translate }}
          </span>
        } @else {
          @for (feature of visibleFeatures(); track feature) {
            <span class="feature-tag" [title]="feature">{{ feature }}</span>
          }
          @if (hiddenCount() > 0) {
            <span class="feature-tag feature-tag--more" [title]="hiddenFeaturesTitle()"> +{{ hiddenCount() }} </span>
          }
        }
      </div>

      <div card-footer class="footer">
        <span class="count">
          {{ 'control.featureFlags.definitions.card.featureCount' | translate: { count: definition().features.length } }}
        </span>
      </div>
    </zag-card>
  `,
  styleUrl: './flag-definition-card.component.scss',
})
export class FlagDefinitionCardComponent {
  readonly definition = input.required<FlagDefinition>();
  readonly edit = output<FlagDefinition>();

  protected readonly visibleFeatures = computed(() => this.definition().features.slice(0, MAX_VISIBLE_FEATURES));
  protected readonly hiddenCount = computed(() => Math.max(0, this.definition().features.length - MAX_VISIBLE_FEATURES));
  protected readonly hiddenFeaturesTitle = computed(() => this.definition().features.slice(MAX_VISIBLE_FEATURES).join(', '));
}
